﻿import {
  GOAL_PROGRESS,
  HOME_LANE_LENGTH,
  RING_LENGTH,
  SEAT_COLORS,
  TRACK_SIZE,
  type CellInfo,
  type CellKind,
  type EffectPreview,
  type SeatColor,
} from './types';

export interface Point {
  x: number;
  y: number;
}

export type FactionSigil = 'blade' | 'reactor' | 'circuit' | 'void';

export interface ColorMeta {
  id: SeatColor;
  label: string;
  shortLabel: string;
  house: string;
  motto: string;
  sigil: FactionSigil;
  startIndex: number;
  base: { x: number; y: number; width: number; height: number };
  lane: Point[];
  goalAnchor: Point;
}

const BASE_SIZE = 180;
export const BOARD_SIZE = 1000;
export const RING_PADDING = 40;
export const RING_STEP = (BOARD_SIZE - RING_PADDING * 2) / 18;
export const RING_CELL_SIZE = RING_STEP - 5;

function lanePoints(xSign: number, ySign: number): Point[] {
  const values = [205, 188, 171, 154, 137, 120, 103];
  return values.map((distance) => ({
    x: 500 + xSign * distance,
    y: 500 + ySign * distance,
  }));
}

export const COLOR_META: Record<SeatColor, ColorMeta> = {
  coral: {
    id: 'coral',
    label: 'Kairo',
    shortLabel: 'Kairo',
    house: 'Nodo Kairo',
    motto: 'Velocidad sin retorno',
    sigil: 'blade',
    startIndex: 2,
    base: { x: 105, y: 105, width: BASE_SIZE, height: BASE_SIZE },
    lane: lanePoints(-1, -1),
    goalAnchor: { x: 388, y: 388 },
  },
  amber: {
    id: 'amber',
    label: 'Helios',
    shortLabel: 'Helios',
    house: 'Nodo Helios',
    motto: 'La luz calcula cada paso',
    sigil: 'reactor',
    startIndex: 19,
    base: { x: 715, y: 105, width: BASE_SIZE, height: BASE_SIZE },
    lane: lanePoints(1, -1),
    goalAnchor: { x: 612, y: 388 },
  },
  jade: {
    id: 'jade',
    label: 'Vireo',
    shortLabel: 'Vireo',
    house: 'Nodo Vireo',
    motto: 'La red crece en silencio',
    sigil: 'circuit',
    startIndex: 36,
    base: { x: 715, y: 715, width: BASE_SIZE, height: BASE_SIZE },
    lane: lanePoints(1, 1),
    goalAnchor: { x: 612, y: 612 },
  },
  azure: {
    id: 'azure',
    label: 'Nébula',
    shortLabel: 'Nébula',
    house: 'Nodo Nébula',
    motto: 'La marea también conquista',
    sigil: 'void',
    startIndex: 53,
    base: { x: 105, y: 715, width: BASE_SIZE, height: BASE_SIZE },
    lane: lanePoints(-1, 1),
    goalAnchor: { x: 388, y: 612 },
  },
};

export const COLOR_ORDER = SEAT_COLORS;

export function colorForSeat(seatIndex: number): SeatColor {
  return SEAT_COLORS[seatIndex % SEAT_COLORS.length] ?? 'coral';
}

export interface GridPoint {
  col: number;
  row: number;
}

export const RING_GRID: GridPoint[] = (() => {
  const cells: GridPoint[] = [];
  for (let col = 0; col < 18; col += 1) cells.push({ col, row: 0 });
  for (let row = 1; row <= 17; row += 1) cells.push({ col: 17, row });
  for (let col = 16; col >= 0; col -= 1) cells.push({ col, row: 17 });
  for (let row = 16; row >= 1; row -= 1) cells.push({ col: 0, row });
  return cells;
})();

export const RING_POSITIONS: Point[] = RING_GRID.map(({ col, row }) => ({
  x: RING_PADDING + (col + 0.5) * RING_STEP,
  y: RING_PADDING + (row + 0.5) * RING_STEP,
}));

function buildCellInfo(): CellInfo[] {
  const cells: CellInfo[] = Array.from({ length: TRACK_SIZE }, (_, index) => ({
    index,
    kind: 'normal' as CellKind,
  }));

  const setCell = (index: number, kind: CellKind, color: SeatColor) => {
    const normalized = ((index % TRACK_SIZE) + TRACK_SIZE) % TRACK_SIZE;
    cells[normalized] = { index: normalized, kind, color };
  };

  for (const color of SEAT_COLORS) {
    const { startIndex } = COLOR_META[color];
    setCell(startIndex, 'start', color);
    setCell(startIndex + 7, 'safe', color);
    setCell(startIndex + 3, 'shortcut', color);
    setCell(startIndex + 9, 'event', color);
    setCell(startIndex + 14, 'risk', color);
  }

  return cells;
}

export const CELLS: CellInfo[] = buildCellInfo();

export function cellAt(index: number): CellInfo {
  return CELLS[((index % TRACK_SIZE) + TRACK_SIZE) % TRACK_SIZE] ?? { index, kind: 'normal' };
}

export function isSafeTrack(index: number): boolean {
  const info = cellAt(index);
  return info.kind === 'start' || info.kind === 'safe';
}

export function trackIndexForProgress(color: SeatColor, progress: number): number | null {
  if (progress < 0 || progress >= RING_LENGTH) return null;
  return (COLOR_META[color].startIndex + progress) % TRACK_SIZE;
}

export function laneIndexForProgress(progress: number): number | null {
  if (progress < RING_LENGTH || progress >= GOAL_PROGRESS) return null;
  return progress - RING_LENGTH;
}

export function lanePoint(color: SeatColor, progress: number): Point | null {
  const laneIndex = laneIndexForProgress(progress);
  if (laneIndex === null) return null;
  return COLOR_META[color].lane[laneIndex] ?? COLOR_META[color].goalAnchor;
}

function occupied(progress: number): boolean {
  return progress >= 0 && progress < GOAL_PROGRESS;
}

export function pieceBoardPoint(color: SeatColor, progress: number, stackIndex = 0, stackCount = 1): Point {
  if (progress === GOAL_PROGRESS) {
    const anchors = COLOR_META[color].goalAnchor;
    return {
      x: anchors.x + (stackIndex % 2 === 0 ? -7 : 7),
      y: anchors.y + (stackIndex > 1 ? 7 : -7),
    };
  }

  if (progress >= RING_LENGTH && progress < GOAL_PROGRESS) {
    return lanePoint(color, progress) ?? COLOR_META[color].goalAnchor;
  }

  if (occupied(progress)) {
    const trackIndex = trackIndexForProgress(color, progress);
    const point = RING_POSITIONS[trackIndex ?? 0] ?? { x: 500, y: 500 };
    if (stackCount <= 1) return point;
    const angle = (Math.PI * 2 * stackIndex) / Math.max(3, stackCount);
    return {
      x: point.x + Math.cos(angle) * 7,
      y: point.y + Math.sin(angle) * 7,
    };
  }

  const meta = COLOR_META[color];
  const slots = [
    { x: meta.base.x + 48, y: meta.base.y + 64 },
    { x: meta.base.x + 132, y: meta.base.y + 64 },
    { x: meta.base.x + 48, y: meta.base.y + 132 },
    { x: meta.base.x + 132, y: meta.base.y + 132 },
  ];
  return slots[stackIndex] ?? slots[0] ?? { x: meta.base.x + 90, y: meta.base.y + 90 };
}

export function baseSlotPoint(color: SeatColor, slot: number): Point {
  return pieceBoardPoint(color, -1, slot, 1);
}

export const CELL_EFFECT_PREVIEWS: Record<'shortcut' | 'event' | 'risk', EffectPreview> = {
  shortcut: {
    kind: 'shortcut',
    title: 'Portal de salto',
    description: 'Teletransporta 3 celdas. El portal no captura unidades ni encadena otra anomalía.',
    icon: 'portal',
  },
  event: {
    kind: 'event',
    title: 'Cofre de anomalías',
    description: 'Abre un cofre sorpresa: impulso, deriva, barrera, sobreimpulso o estasis.',
    icon: 'spark',
  },
  risk: {
    kind: 'risk',
    title: 'Anomalía de fisura',
    description: 'Retrocede 3 celdas. La fisura no captura unidades ni encadena otra anomalía.',
    icon: 'rift',
  },
};

export function effectPreviewForTrack(index: number, enabled: boolean): EffectPreview | null {
  if (!enabled) return null;
  const info = cellAt(index);
  if (info.kind === 'shortcut' || info.kind === 'event' || info.kind === 'risk') {
    return CELL_EFFECT_PREVIEWS[info.kind];
  }
  return null;
}

export const BOARD_LEGEND: { kind: CellKind; title: string; description: string; icon: string }[] = [
  {
    kind: 'start',
    title: 'Muelle de salto',
    description: 'Plataforma segura de cada escuadrón. Con un 5 despliegas una unidad desde el muelle.',
    icon: 'flag',
  },
  {
    kind: 'safe',
    title: 'Zona segura',
    description: 'Las unidades situadas aquí no pueden ser capturadas.',
    icon: 'shield',
  },
  {
    kind: 'shortcut',
    title: 'Portal de salto',
    description: 'Teletransporta 3 celdas extra. No captura ni provoca otra anomalía.',
    icon: 'portal',
  },
  {
    kind: 'event',
    title: 'Cofre de anomalías',
    description: 'Abre un efecto sorpresa de una lista equilibrada y visible.',
    icon: 'spark',
  },
  {
    kind: 'risk',
    title: 'Anomalía de fisura',
    description: 'Retrocede 3 celdas. No captura ni provoca otra anomalía.',
    icon: 'rift',
  },
];

export const TRACK_LENGTH = TRACK_SIZE;
export const TOTAL_TRAVEL = GOAL_PROGRESS;
export { HOME_LANE_LENGTH };


